import { Trans } from "@lingui/react/macro";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";

import type { DataTableRow } from "../../../components/ui-kit/tables/data-table/table-features";

import { apiGetPatients, PatientStatus } from "../../../api";
import { reportError } from "../../../app/errors";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { patientStatuses } from "../../../constants/enum-to-text";

/**
 * Compact per-physician patient breakdown, e.g. "15 — 12 Active · 2 Not
 * active · 1 Deceased". Shows the plain total while the index is loading.
 */
export const PatientStatusCell = observer(function PatientStatusCell({
  patients,
  index,
}: {
  patients: Array<{ patient_id: number }> | null | undefined;
  index: PatientStatusIndex;
}) {
  const total = patients?.length ?? 0;
  if (!index.initialized || total === 0) return <>{total}</>;

  const counts = countPatientStatuses(patients, index);
  const parts = statusEntries()
    .filter(([status]) => (counts.get(status) ?? 0) > 0)
    .map(([status, label]) => `${counts.get(status)} ${label}`);
  return (
    <>
      {total}
      {parts.length > 0 && (
        <span className="text-muted-foreground"> — {parts.join(" · ")}</span>
      )}
    </>
  );
});

/**
 * Table footer with per-status patient totals over the rows matching the
 * active filters. Patients are deduplicated by id — a patient assigned to
 * several listed physicians counts once.
 *
 * Takes the filtered rows (not the table instance) so the observer's memo
 * sees a prop change whenever the filters change — TanStack replaces the
 * rows array on every filter/data update, while the table reference is
 * stable.
 */
export const PatientStatusTotalsRow = observer(function PatientStatusTotalsRow<
  T extends { patients?: Array<{ patient_id: number }> | null },
>({
  rows,
  colSpan,
  index,
}: {
  rows: Array<DataTableRow<T>>;
  colSpan: number;
  index: PatientStatusIndex;
}) {
  const statusById = new Map<number, PatientStatus | undefined>();
  for (const row of rows) {
    for (const p of row.original.patients ?? []) {
      statusById.set(p.patient_id, index.statusByPatientId.get(p.patient_id));
    }
  }
  const counts = new Map<PatientStatus, number>();
  for (const status of statusById.values()) {
    if (status == null) continue;
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }

  return (
    <tr>
      <td colSpan={colSpan} className="text-left font-medium">
        {/* Stick the summary to the viewport's left edge so it stays visible
            while the wide table scrolls horizontally. `left-2` matches the
            cell's own left padding (0.5rem) so it pins with no initial drift. */}
        <div className="sticky left-2 inline-block">
          <Trans>Patients of listed physicians: {statusById.size}</Trans>
          {index.initialized && (
            <span className="font-normal text-muted-foreground">
              {" ("}
              {statusEntries()
                .map(
                  ([status, label]) => `${label}: ${counts.get(status) ?? 0}`,
                )
                .join(" · ")}
              {")"}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
});

/**
 * Maps patient_id → patient_status for all patients the current user can see.
 * Physician responses only carry bare patient references (no status), so
 * status breakdowns join through this index client-side.
 */
export class PatientStatusIndex {
  statusByPatientId = new Map<number, PatientStatus>();
  initialized = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetches all visible patients and indexes them. Failures are reported
   * (Sentry/console) but not surfaced — the breakdown is supplementary, so
   * the UI just keeps showing plain totals.
   */
  async load() {
    try {
      const res = await apiGetPatients({
        query: { load_type: "basic", paginate: false },
      });
      this.indexPatients(
        res.data.patients as Array<{
          patient_id: number;
          patient_status?: PatientStatus | null;
        }>,
      );
    } catch (error) {
      reportError(error);
    }
  }

  /**
   * Populates the index from an already-fetched patient list — lets a route
   * that fetches all patients anyway feed the index without a second call.
   */
  indexPatients(
    patients: Array<{
      patient_id: number;
      patient_status?: PatientStatus | null;
    }>,
  ) {
    this.statusByPatientId = new Map(
      patients.flatMap((p) =>
        p.patient_status != null ? [[p.patient_id, p.patient_status]] : [],
      ),
    );
    this.initialized = true;
  }
}

export function countPatientStatuses(
  patients: Array<{ patient_id: number }> | null | undefined,
  index: PatientStatusIndex,
): Map<PatientStatus, number> {
  const counts = new Map<PatientStatus, number>();
  for (const p of patients ?? []) {
    const status = index.statusByPatientId.get(p.patient_id);
    if (status == null) continue;
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return counts;
}

/**
 * All statuses in enum order with their labels. Derived from the
 * `patientStatuses` record so newly added backend statuses show up everywhere
 * once the label map knows them.
 */
function statusEntries(): Array<[PatientStatus, string]> {
  return Object.entries(patientStatuses).map(([status, label]) => [
    Number(status),
    resolveEnumLabel(label),
  ]);
}
